import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { FlightProfile } from "../../core/decorFlights";

const SLOPE = Math.PI / 6;
const LANDING = 1.6;
const STEP_RISE = 0.2;
const TRUSS_DEPTH = 0.9;
const BALUSTRADE_HEIGHT = 1;
const BALUSTRADE_WIDTH = 0.12;
const STEP_SPEED = 0.5;
/** Échelle d'une marche entièrement rentrée : nulle, la matrice ne s'inverserait plus. */
const FLAT = 0.001;

export const DEFAULT_ESCALATOR_RISE = 5;

const metal = new THREE.MeshStandardMaterial({ name: "steps", color: "#8a8f96", roughness: 0.5, metalness: 0.6 });
const nosing = new THREE.MeshStandardMaterial({ name: "nosing", color: "#f2c230", roughness: 0.6, metalness: 0.1 });
const truss = new THREE.MeshStandardMaterial({ name: "truss", color: "#d3d6da", roughness: 0.6, metalness: 0.3 });
const glass = new THREE.MeshStandardMaterial({
  name: "glass",
  color: "#cfe6f0",
  roughness: 0.1,
  metalness: 0,
  transparent: true,
  opacity: 0.35,
  depthWrite: false,
  side: THREE.DoubleSide,
});
const handrail = new THREE.MeshStandardMaterial({ name: "handrail", color: "#202326", roughness: 0.5, metalness: 0.2 });

function box(width: number, height: number, depth: number, x: number, y: number, z: number, tiltX = 0): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  if (tiltX) geometry.rotateX(tiltX);
  geometry.translate(x, y, z);
  return geometry;
}

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

/** Hauteur de l'escalator le long de sa descente, paliers compris : voir `FlightProfile`. */
export function openEscalatorProfile(rise: number): FlightProfile {
  const run = rise / Math.tan(SLOPE);
  return [[0, rise], [LANDING, rise], [LANDING + run, 0], [2 * LANDING + run, 0]];
}

/** Longueur au sol d'un escalator en plein air de ce dénivelé, paliers compris. */
export function openEscalatorLength(rise: number): number {
  return rise / Math.tan(SLOPE) + 2 * LANDING;
}

/**
 * Escalator posé à l'air libre, du sol jusqu'à `rise` mètres plus haut : on y entre côté −z par le
 * palier du haut, il descend vers +z jusqu'au palier du bas, posé au sol. Mesures en mètres. Son clip
 * `idle` fait monter les marches ; joué à l'envers, elles descendent.
 */
export function buildOpenEscalator(width: number, rise: number): THREE.Group {
  const run = rise / Math.tan(SLOPE);
  const z0 = -openEscalatorLength(rise) / 2;
  const slopeStart = z0 + LANDING;
  const slopeEnd = slopeStart + run;
  const hypotenuse = Math.hypot(run, rise);
  const middleZ = (slopeStart + slopeEnd) / 2;
  const tread = width - 2 * BALUSTRADE_WIDTH;
  const sides = [-1, 1].map((side) => side * (width / 2 - BALUSTRADE_WIDTH / 2));

  const steps = Math.max(2, Math.round(rise / STEP_RISE));
  const stepRun = run / steps;
  const stepRise = rise / steps;
  const treads: THREE.BufferGeometry[] = [];
  const noses: THREE.BufferGeometry[] = [];
  for (let i = 1; i < steps; i++) {
    const top = rise - (i + 1) * stepRise;
    const z = slopeStart + i * stepRun;
    treads.push(box(tread, stepRise, stepRun, 0, top + stepRise / 2, z + stepRun / 2));
    noses.push(box(tread, 0.01, 0.04, 0, top + stepRise + 0.005, z + 0.02));
  }
  // Une marche seule, centrée sur son origine, pour les deux bouts qui changent de taille.
  const loneStep = (name: string, slot: number, scaleY: number) => {
    const group = new THREE.Group();
    group.name = name;
    group.add(
      merged([box(tread, stepRise, stepRun, 0, 0, 0)], metal, `${name}Tread`),
      merged([box(tread, 0.01, 0.04, 0, stepRise / 2 + 0.005, -stepRun / 2 + 0.02)], nosing, `${name}Nosing`),
    );
    group.position.set(0, rise - (slot + 1) * stepRise + stepRise / 2, slopeStart + slot * stepRun + stepRun / 2);
    group.scale.set(1, scaleY, 1);
    return group;
  };
  const topStep = loneStep("TopStep", 0, 1);
  // Sous le sol au repos : `Pit` l'écarte des bornes du modèle, qui le poseraient plus haut.
  const bottomStep = loneStep("PitBottomStep", steps, FLAT);
  const landings = [box(tread, 0.05, LANDING, 0, rise - 0.025, z0 + LANDING / 2), box(tread, 0.05, LANDING, 0, 0.025, slopeEnd + LANDING / 2)];

  // Profil de la poutre, dans le plan (z, y) : sous le palier du haut puis sous la pente, jusqu'au sol.
  const profile = new THREE.Shape([
    new THREE.Vector2(z0, rise),
    new THREE.Vector2(slopeStart, rise),
    new THREE.Vector2(slopeEnd, 0),
    new THREE.Vector2(slopeEnd - TRUSS_DEPTH / Math.tan(SLOPE), 0),
    new THREE.Vector2(slopeStart, rise - TRUSS_DEPTH),
    new THREE.Vector2(z0, rise - TRUSS_DEPTH),
  ]);
  const body = [
    new THREE.ExtrudeGeometry(profile, { depth: width, bevelEnabled: false }).rotateY(-Math.PI / 2).translate(width / 2, 0, 0),
  ];

  const panels: THREE.BufferGeometry[] = [];
  const rails: THREE.BufferGeometry[] = [];
  for (const x of sides) {
    panels.push(
      box(0.03, BALUSTRADE_HEIGHT, hypotenuse, x, rise / 2 + BALUSTRADE_HEIGHT / 2, middleZ, SLOPE),
      box(0.03, BALUSTRADE_HEIGHT, LANDING, x, rise + BALUSTRADE_HEIGHT / 2, z0 + LANDING / 2),
      box(0.03, BALUSTRADE_HEIGHT, LANDING, x, BALUSTRADE_HEIGHT / 2, slopeEnd + LANDING / 2),
    );
    rails.push(
      box(BALUSTRADE_WIDTH, 0.08, hypotenuse, x, rise / 2 + BALUSTRADE_HEIGHT, middleZ, SLOPE),
      box(BALUSTRADE_WIDTH, 0.08, LANDING, x, rise + BALUSTRADE_HEIGHT, z0 + LANDING / 2),
      box(BALUSTRADE_WIDTH, 0.08, LANDING, x, BALUSTRADE_HEIGHT, slopeEnd + LANDING / 2),
    );
  }

  // Les marches de la pente glissent d'un cran puis reviennent : identiques, la boucle ne se voit pas.
  const moving = new THREE.Group();
  moving.name = "MovingSteps";
  moving.add(merged(treads, metal, "Steps"), merged(noses, nosing, "Nosing"));
  // La marche du haut s'efface dans le palier pendant qu'une autre sort du bas : rien ne saute quand la
  // boucle repart.
  const period = Math.hypot(stepRun, stepRise) / STEP_SPEED;
  const times = [0, period];
  const slide = (from: THREE.Vector3) => [...from.toArray(), from.x, from.y + stepRise, from.z - stepRun];
  const tracks = [
    new THREE.VectorKeyframeTrack("MovingSteps.position", times, [0, 0, 0, 0, stepRise, -stepRun]),
    new THREE.VectorKeyframeTrack("TopStep.position", times, slide(topStep.position)),
    new THREE.VectorKeyframeTrack("TopStep.scale", times, [1, 1, 1, 1, FLAT, 1]),
    new THREE.VectorKeyframeTrack("PitBottomStep.position", times, slide(bottomStep.position)),
    new THREE.VectorKeyframeTrack("PitBottomStep.scale", times, [1, FLAT, 1, 1, 1, 1]),
  ];

  const root = new THREE.Group();
  root.add(
    moving,
    topStep,
    bottomStep,
    merged(landings, metal, "Landings"),
    merged(body, truss, "Truss"),
    merged(panels, glass, "Glass"),
    merged(rails, handrail, "Handrails"),
  );
  root.animations = [new THREE.AnimationClip("idle", period, tracks)];
  return root;
}
